'use client';

import { Button } from '@app/app/_components/Button/Button';
import { ChoiceButtonInput } from '@app/app/_components/Input/ChoiceButtonInput';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { Message } from '@app/app/_components/Message/Message';
import { ArrowLeftIcon } from '@app/app/_icons/ArrowLeftIcon';
import { CompanyIcon } from '@app/app/_icons/CompanyIcon';
import { PrivatePersonIcon } from '@app/app/_icons/PrivatePersonIcon';
import { Client } from '@app/domain/model/Client';
import { ClientType } from '@app/domain/model/ClientType';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { selectClient } from '@app/service/auth/actions/selectClient';
import { logout } from '@app/service/auth/actions/logout';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useFormState } from 'react-dom';

interface Props {
    clients: Client[];
    canGoBack?: boolean;
    onSuccess?: () => void;
    onBack?: () => void;
}

export function SelectClientWidget({ clients, canGoBack = false, onSuccess, onBack }: Props) {
    const t = useTranslations();
    const router = useRouter();

    const formRef = useRef<HTMLFormElement>(null);

    const [pending, setPending] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const [formState, formAction] = useFormState(selectClient, { status: FormStatus.Pending });

    const handleChange = useCallback(() => {
        setPending(false);
    }, []);

    const handleSubmit = useCallback(() => {
        setIsLoading(true);
        formRef.current?.requestSubmit();
    }, []);

    const handleGoBack = useCallback(async () => {
        if (onBack) {
            await logout();
            onBack();
        } else {
            await logout();
            router.push('/auth/login');
        }
    }, [onBack, router]);

    useEffect(() => {
        if (formState.status === FormStatus.Success) {
            return onSuccess?.();
        }
        if (formState.status === FormStatus.Error) {
            setIsLoading(false);
        }
    }, [formState.status, onSuccess]);

    return (
        <>
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    {canGoBack ? (
                        <GhostButton onClick={handleGoBack}>
                            <ArrowLeftIcon className="size-6" />
                        </GhostButton>
                    ) : (
                        <br />
                    )}
                    <h1 className="text-center text-3xl font-medium">{t('auth.selectAccountTitle')}</h1>
                    <br />
                </div>
                <p className="text-center text-neutral-500">{t('auth.selectAccountDescription')}</p>
            </div>

            <form ref={formRef} action={formAction} className="flex flex-col gap-8">
                {formState.status === FormStatus.Error && <Message>{t(formState.message)}</Message>}

                <ChoiceButtonInput
                    name="clientId"
                    options={clients.map(client => ({
                        value: client.id,
                        label: (
                            <div className="flex items-center gap-4">
                                {client.type === ClientType.Person ? (
                                    <PrivatePersonIcon className="size-10 text-primary-500" />
                                ) : (
                                    <CompanyIcon className="size-10 text-primary-500" />
                                )}
                                <div className="flex flex-col gap-1">
                                    <div className="font-medium">{client.fullName}</div>
                                    <div className="text-sm text-neutral-500">
                                        {client.type === ClientType.Person
                                            ? t('auth.privatePerson')
                                            : t('auth.company')}
                                    </div>
                                </div>
                            </div>
                        ),
                    }))}
                    error={formState.violations?.clientId}
                    onChange={handleChange}
                />

                <Button onClick={handleSubmit} disabled={pending} isLoading={isLoading}>
                    {t('general.continue')}
                </Button>
            </form>
        </>
    );
}
